import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Loader2, Send, CheckCircle2, AlertCircle } from "lucide-react";
import { submitGHLContact } from "@/lib/ghl.functions";
import { APPROX_OWED_OPTIONS } from "@/lib/ghl.schema";

interface Props {
  language?: "en" | "es";
  /** Visual variant — `dark` for navy-card placements, `light` for white cards. */
  tone?: "dark" | "light";
}

// Spanish labels for the same canonical English GHL values.
const OWED_LABELS_ES: Record<(typeof APPROX_OWED_OPTIONS)[number], string> = {
  "Less than $10,000": "Menos de $10,000",
  "$10,000 - $20,000": "$10,000 – $20,000",
  "$20,000 - $50,000": "$20,000 – $50,000",
  "$50,000 - $100,000": "$50,000 – $100,000",
  "$100,000-$250,000": "$100,000 – $250,000",
  "$250,000-$1,000,000+": "$250,000 – $1,000,000+",
  "Not sure yet...": "No estoy seguro/a",
};

const COPY = {
  en: {
    firstName: "First name",
    lastName: "Last name",
    email: "Email address",
    phone: "Phone number",
    referred: "Were you referred by your CPA or Attorney?",
    owed: "Approximately how much do you owe?",
    unfiled: "How many years of unfiled taxes do you think you have?",
    unfiledPlaceholder: "e.g. 3",
    garnished: "Have your wages been garnished by the IRS yet?",
    audited: "Are you being audited by either the IRS or State?",
    letters: "Are you receiving threatening letters from the IRS?",
    additional: "Any additional information about your case?",
    additionalPlaceholder:
      "Notices received, deadlines, spouse involvement, business vs personal, etc.",
    submit: "Request my free case review",
    submitting: "Sending…",
    success: "Thanks — a CPA will call you back, usually same business day.",
    errorTitle: "We couldn't submit your request",
    errorBody: "Please try again or call (214) 646-3802.",
    consent:
      "By submitting, you agree we may contact you about your inquiry. Confidential under IRS §7216.",
    yes: "Yes",
    no: "No",
    selectPlaceholder: "—",
    required: "Required",
    errors: {
      firstName: "Please enter your first name.",
      lastName: "Please enter your last name.",
      email: "Please enter a valid email address.",
      phone: "Please enter a valid phone number (at least 7 digits).",
      referred: "Please select yes or no.",
      owed: "Please choose an approximate amount.",
      unfiled: "Please enter a whole number of years (0–99).",
      garnished: "Please select yes or no.",
      audited: "Please select yes or no.",
      letters: "Please select yes or no.",
    },
  },
  es: {
    firstName: "Nombre",
    lastName: "Apellido",
    email: "Correo electrónico",
    phone: "Número de teléfono",
    referred: "¿Fue referido por su CPA o abogado?",
    owed: "¿Aproximadamente cuánto debe?",
    unfiled: "¿Cuántos años de impuestos sin presentar cree tener?",
    unfiledPlaceholder: "ej. 3",
    garnished: "¿El IRS ya ha embargado su salario?",
    audited: "¿Está siendo auditado por el IRS o el Estado?",
    letters: "¿Recibe cartas amenazantes del IRS?",
    additional: "¿Alguna información adicional sobre su caso?",
    additionalPlaceholder:
      "Avisos recibidos, plazos, cónyuge involucrado, negocio vs. personal, etc.",
    submit: "Solicitar mi revisión gratis",
    submitting: "Enviando…",
    success: "Gracias — un CPA le devolverá la llamada, generalmente el mismo día.",
    errorTitle: "No pudimos enviar su solicitud",
    errorBody: "Intente de nuevo o llame al (214) 646-3802.",
    consent:
      "Al enviar, acepta que podemos contactarle sobre su consulta. Confidencial bajo IRS §7216.",
    yes: "Sí",
    no: "No",
    selectPlaceholder: "—",
    required: "Requerido",
    errors: {
      firstName: "Por favor ingrese su nombre.",
      lastName: "Por favor ingrese su apellido.",
      email: "Por favor ingrese un correo electrónico válido.",
      phone: "Por favor ingrese un número de teléfono válido (mínimo 7 dígitos).",
      referred: "Por favor seleccione sí o no.",
      owed: "Por favor elija una cantidad aproximada.",
      unfiled: "Ingrese un número entero de años (0–99).",
      garnished: "Por favor seleccione sí o no.",
      audited: "Por favor seleccione sí o no.",
      letters: "Por favor seleccione sí o no.",
    },
  },
} as const;

function getUtm() {
  if (typeof window === "undefined") return {};
  const p = new URLSearchParams(window.location.search);
  return {
    utmSource: p.get("utm_source") || undefined,
    utmMedium: p.get("utm_medium") || undefined,
    utmCampaign: p.get("utm_campaign") || undefined,
  };
}

type FormShape = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  referredByCpaAttorney: "" | "Yes" | "No";
  approxOwed: "" | (typeof APPROX_OWED_OPTIONS)[number];
  unfiledYears: string;
  wagesGarnished: "" | "Yes" | "No";
  audited: "" | "Yes" | "No";
  threateningLetters: "" | "Yes" | "No";
  additionalInfo: string;
};

type FieldKey = keyof FormShape;
type FieldErrors = Partial<Record<FieldKey, string>>;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validate(form: FormShape, errs: Record<string, string>): FieldErrors {
  const out: FieldErrors = {};
  if (!form.firstName.trim()) out.firstName = errs.firstName;
  if (!form.lastName.trim()) out.lastName = errs.lastName;
  if (!EMAIL_RE.test(form.email.trim())) out.email = errs.email;
  if (form.phone.replace(/\D/g, "").length < 7) out.phone = errs.phone;
  if (!form.referredByCpaAttorney) out.referredByCpaAttorney = errs.referred;
  if (!form.approxOwed) out.approxOwed = errs.owed;
  if (!/^\d{1,2}$/.test(form.unfiledYears.trim())) out.unfiledYears = errs.unfiled;
  if (!form.wagesGarnished) out.wagesGarnished = errs.garnished;
  if (!form.audited) out.audited = errs.audited;
  if (!form.threateningLetters) out.threateningLetters = errs.letters;
  return out;
}

export const CaseReviewForm = ({ language = "en", tone = "dark" }: Props) => {
  const t = COPY[language];
  const submit = useServerFn(submitGHLContact);

  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [touched, setTouched] = useState<Partial<Record<FieldKey, boolean>>>({});
  const [form, setForm] = useState<FormShape>({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    referredByCpaAttorney: "",
    approxOwed: "",
    unfiledYears: "",
    wagesGarnished: "",
    audited: "",
    threateningLetters: "",
    additionalInfo: "",
  });

  const update = (key: FieldKey, value: string) => {
    const next = { ...form, [key]: value } as FormShape;
    setForm(next);
    if (errors[key]) {
      const nextErrs = validate(next, t.errors);
      setErrors((prev) => ({ ...prev, [key]: nextErrs[key] }));
    }
  };

  const onBlur = (key: FieldKey) => () => {
    setTouched((p) => ({ ...p, [key]: true }));
    const nextErrs = validate(form, t.errors);
    setErrors((prev) => ({ ...prev, [key]: nextErrs[key] }));
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setSubmitError(null);

    const fieldErrors = validate(form, t.errors);
    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors);
      setTouched(
        Object.keys(fieldErrors).reduce(
          (acc, k) => ({ ...acc, [k]: true }),
          {} as Partial<Record<FieldKey, boolean>>,
        ),
      );
      const firstKey = Object.keys(fieldErrors)[0] as FieldKey;
      const el = document.getElementById(`cr-${firstKey}`);
      el?.focus();
      return;
    }

    setBusy(true);
    try {
      await submit({
        data: {
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          referredByCpaAttorney: form.referredByCpaAttorney as "Yes" | "No",
          approxOwed: form.approxOwed as (typeof APPROX_OWED_OPTIONS)[number],
          unfiledYears: form.unfiledYears.trim(),
          wagesGarnished: form.wagesGarnished as "Yes" | "No",
          audited: form.audited as "Yes" | "No",
          threateningLetters: form.threateningLetters as "Yes" | "No",
          additionalInfo: form.additionalInfo,
          language,
          sourcePath: typeof window !== "undefined" ? window.location.pathname : undefined,
          ...getUtm(),
        },
      });
      setDone(true);
      if (typeof window !== "undefined") {
        window.location.assign("/thank-you");
      }
    } catch {
      setSubmitError(t.errorBody);
    } finally {
      setBusy(false);
    }
  };

  const fieldClass = (hasError: boolean) => {
    const base =
      tone === "dark"
        ? "w-full rounded-md bg-white/95 px-4 py-3 text-sm text-navy-dark placeholder:text-slate-500 focus:outline-none focus:ring-2 border"
        : "w-full rounded-md bg-white px-4 py-3 text-sm text-navy-dark placeholder:text-slate-500 focus:outline-none focus:ring-2 border";
    if (hasError) return `${base} border-red-500 focus:ring-red-500`;
    return tone === "dark"
      ? `${base} border-white/10 focus:ring-gold`
      : `${base} border-border focus:ring-navy`;
  };

  const labelClass =
    tone === "dark"
      ? "block text-[11px] font-bold uppercase tracking-[0.14em] text-white/80 mb-1.5"
      : "block text-[11px] font-bold uppercase tracking-[0.14em] text-muted-foreground mb-1.5";

  const errorTextClass =
    tone === "dark"
      ? "mt-1.5 flex items-start gap-1.5 text-xs font-medium text-red-200"
      : "mt-1.5 flex items-start gap-1.5 text-xs font-medium text-red-600";

  const showErr = (k: FieldKey): boolean => !!touched[k] && !!errors[k];

  const renderError = (k: FieldKey) =>
    showErr(k) ? (
      <p id={`cr-${k}-error`} className={errorTextClass}>
        <AlertCircle className="h-3.5 w-3.5 flex-shrink-0 mt-px" />
        <span>{errors[k]}</span>
      </p>
    ) : null;

  if (done) {
    return (
      <div
        role="status"
        aria-live="polite"
        className={`rounded-md p-6 text-center ${
          tone === "dark" ? "bg-white/10 text-white" : "bg-secondary text-navy-dark"
        }`}
      >
        <CheckCircle2 className="mx-auto h-10 w-10 text-gold mb-3" />
        <p className="font-heading text-lg font-bold">{t.success}</p>
      </div>
    );
  }

  // Plain render function (NOT a component) — defining a component inline
  // recreates its type each render, remounting the inputs and swallowing the
  // first click.
  const renderYesNo = (
    field: "referredByCpaAttorney" | "wagesGarnished" | "audited" | "threateningLetters",
    label: string,
  ) => {
    const value = form[field];
    const err = showErr(field);
    const optBase =
      tone === "dark"
        ? "flex-1 cursor-pointer rounded-md border px-4 py-2.5 text-sm font-semibold text-center transition-colors"
        : "flex-1 cursor-pointer rounded-md border px-4 py-2.5 text-sm font-semibold text-center transition-colors";
    const selectedDark = "border-gold bg-gold text-navy-dark";
    const selectedLight = "border-navy bg-navy text-white";
    const unselectedDark = "border-white/20 bg-white/5 text-white hover:bg-white/10";
    const unselectedLight = "border-border bg-white text-navy-dark hover:bg-slate-50";
    const errBorder = err ? " ring-1 ring-red-500" : "";

    return (
      <div>
        <span className={labelClass} id={`cr-${field}-label`}>
          {label}
        </span>
        <div
          role="radiogroup"
          aria-labelledby={`cr-${field}-label`}
          aria-invalid={err}
          className={`flex gap-2${errBorder}`}
          id={`cr-${field}`}
          tabIndex={-1}
        >
          {(["Yes", "No"] as const).map((v) => {
            const selected = value === v;
            const cls = selected
              ? tone === "dark"
                ? `${optBase} ${selectedDark}`
                : `${optBase} ${selectedLight}`
              : tone === "dark"
                ? `${optBase} ${unselectedDark}`
                : `${optBase} ${unselectedLight}`;
            return (
              <label key={v} className={cls}>
                <input
                  type="radio"
                  name={field}
                  value={v}
                  checked={selected}
                  onChange={() => {
                    update(field, v);
                    setTouched((p) => ({ ...p, [field]: true }));
                  }}
                  className="sr-only"
                />
                {v === "Yes" ? t.yes : t.no}
              </label>
            );
          })}
        </div>
        {renderError(field)}
      </div>
    );
  };

  return (
    <form action="/api/public/submit-contact" method="post" onSubmit={onSubmit} className="grid gap-4" noValidate>
      <input type="hidden" name="language" value={language} />
      {/* Name */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="cr-firstName" className={labelClass}>
            {t.firstName}
          </label>
          <input
            id="cr-firstName"
            name="firstName"
            type="text"
            autoComplete="given-name"
            value={form.firstName}
            onChange={(e) => update("firstName", e.target.value)}
            onBlur={onBlur("firstName")}
            aria-invalid={showErr("firstName")}
            aria-describedby={showErr("firstName") ? "cr-firstName-error" : undefined}
            maxLength={60}
            className={fieldClass(showErr("firstName"))}
          />
          {renderError("firstName")}
        </div>
        <div>
          <label htmlFor="cr-lastName" className={labelClass}>
            {t.lastName}
          </label>
          <input
            id="cr-lastName"
            name="lastName"
            type="text"
            autoComplete="family-name"
            value={form.lastName}
            onChange={(e) => update("lastName", e.target.value)}
            onBlur={onBlur("lastName")}
            aria-invalid={showErr("lastName")}
            aria-describedby={showErr("lastName") ? "cr-lastName-error" : undefined}
            maxLength={60}
            className={fieldClass(showErr("lastName"))}
          />
          {renderError("lastName")}
        </div>
      </div>

      {/* Contact */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="cr-email" className={labelClass}>
            {t.email}
          </label>
          <input
            id="cr-email"
            name="email"
            type="email"
            autoComplete="email"
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            onBlur={onBlur("email")}
            aria-invalid={showErr("email")}
            aria-describedby={showErr("email") ? "cr-email-error" : undefined}
            maxLength={255}
            className={fieldClass(showErr("email"))}
          />
          {renderError("email")}
        </div>
        <div>
          <label htmlFor="cr-phone" className={labelClass}>
            {t.phone}
          </label>
          <input
            id="cr-phone"
            name="phone"
            type="tel"
            autoComplete="tel"
            inputMode="tel"
            value={form.phone}
            onChange={(e) => update("phone", e.target.value)}
            onBlur={onBlur("phone")}
            aria-invalid={showErr("phone")}
            aria-describedby={showErr("phone") ? "cr-phone-error" : undefined}
            maxLength={40}
            className={fieldClass(showErr("phone"))}
          />
          {renderError("phone")}
        </div>
      </div>

      {/* Referred */}
      {renderYesNo("referredByCpaAttorney", t.referred)}

      {/* Owed */}
      <div>
        <label htmlFor="cr-approxOwed" className={labelClass}>
          {t.owed}
        </label>
        <select
          id="cr-approxOwed"
          name="approxOwed"
          value={form.approxOwed}
          onChange={(e) => update("approxOwed", e.target.value)}
          onBlur={onBlur("approxOwed")}
          aria-invalid={showErr("approxOwed")}
          className={fieldClass(showErr("approxOwed"))}
        >
          <option value="">{t.selectPlaceholder}</option>
          {APPROX_OWED_OPTIONS.map((opt) => (
            <option key={opt} value={opt}>
              {language === "es" ? OWED_LABELS_ES[opt] : opt}
            </option>
          ))}
        </select>
        {renderError("approxOwed")}
      </div>

      {/* Unfiled years */}
      <div>
        <label htmlFor="cr-unfiledYears" className={labelClass}>
          {t.unfiled}
        </label>
        <input
          id="cr-unfiledYears"
          name="unfiledYears"
          type="number"
          inputMode="numeric"
          min={0}
          max={99}
          step={1}
          placeholder={t.unfiledPlaceholder}
          value={form.unfiledYears}
          onChange={(e) => update("unfiledYears", e.target.value.replace(/\D/g, "").slice(0, 2))}
          onBlur={onBlur("unfiledYears")}
          aria-invalid={showErr("unfiledYears")}
          className={fieldClass(showErr("unfiledYears"))}
        />
        {renderError("unfiledYears")}
      </div>

      {/* Yes/No trio */}
      {renderYesNo("wagesGarnished", t.garnished)}
      {renderYesNo("audited", t.audited)}
      {renderYesNo("threateningLetters", t.letters)}

      {/* Additional info */}
      <div>
        <label htmlFor="cr-additionalInfo" className={labelClass}>
          {t.additional}
        </label>
        <textarea
          id="cr-additionalInfo"
          name="additionalInfo"
          rows={4}
          placeholder={t.additionalPlaceholder}
          value={form.additionalInfo}
          onChange={(e) => update("additionalInfo", e.target.value)}
          maxLength={4000}
          className={fieldClass(false)}
        />
      </div>

      {submitError && (
        <div
          role="alert"
          className={`flex items-start gap-2 rounded-md border px-3 py-2.5 text-sm ${
            tone === "dark"
              ? "border-red-400/40 bg-red-500/10 text-red-100"
              : "border-red-200 bg-red-50 text-red-700"
          }`}
        >
          <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">{t.errorTitle}</p>
            <p className="text-xs opacity-90">{submitError}</p>
          </div>
        </div>
      )}

      <button
        type="submit"
        disabled={busy}
        className="inline-flex items-center justify-center gap-2 rounded-md bg-gold px-6 py-3.5 text-sm font-bold uppercase tracking-wider text-navy-dark shadow-lg transition-transform hover:scale-[1.01] hover:bg-gold-light disabled:opacity-70"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        {busy ? t.submitting : t.submit}
      </button>
      <p
        className={`text-xs ${
          tone === "dark" ? "text-white/60" : "text-muted-foreground"
        }`}
      >
        {t.consent}
      </p>
    </form>
  );
};

export default CaseReviewForm;
